/**
 * 画面と論理座標の変換、そしてカメラ操作。
 * ビルは縦に長いので縦移動が主だが、拡大中は横にも動かせる（§5.3）。
 * sim はこのファイルの存在を知らない。
 */
import { TILE } from '../sim/grid';

export interface Camera {
  /** 論理px → CSS px の倍率 */
  scale: number;
  /** 表示している論理座標の左上 */
  x: number;
  y: number;
  /** 慣性（論理px/秒） */
  vx: number;
  vy: number;
  /** 追いかけている猫の id。指で動かすと解除される */
  follow: number | null;
  /** ズーム段階の番号 */
  zoom: number;
}

export interface Viewport {
  cssW: number;
  cssH: number;
  dpr: number;
}

/**
 * 全体表示 → 等倍 → 2倍。全体表示は盤面の幅に合わせる。
 * 既定は**等倍**（1）にしてある。このゲームは寄って眺めるものなので、
 * 開いた時に全体像から始めると、猫の顔も落とし物も読めない（§2.1 絵で読ませる）。
 */
export const ZOOM_STEPS = 3;
export const ZOOM_LABEL = ['拡大', '2倍', '全体'];
export const DEFAULT_ZOOM = 1;

/**
 * 指を離したあとの滑り。1秒でこれだけ残る。
 * ここを小さくしすぎると、フリックしてもほとんど進まない。
 * 滑る距離はだいたい「離した時の速度 ÷ ln(1/FRICTION)」。
 */
const FRICTION = 0.08;
/**
 * フリックの勢い。指の速度をそのまま使うと物足りないので少し盛る。
 * ビルが縦に長いので、1回のフリックで1画面ぶんは越えてほしい。
 */
const FLICK_BOOST = 2.2;
/** 滑りの上限（論理px/秒）。速すぎると自分がどこを見ているか分からなくなる */
const MAX_FLING = 12000;
/** これ以下の速度は止まったものとみなす */
const MIN_SPEED = 4;
/** 追従の追いつき具合 */
const FOLLOW_LERP = 6;

export function createCamera(): Camera {
  return { scale: 1, x: 0, y: 0, vx: 0, vy: 0, follow: null, zoom: DEFAULT_ZOOM };
}

export function worldSize(cols: number, rows: number): { w: number; h: number } {
  return { w: cols * TILE, h: rows * TILE };
}

/** 全体表示。ビルが縦に長いので、幅だけでなく高さも収める */
export function fitScale(view: Viewport, cols: number, rows: number): number {
  return Math.min(view.cssW / (cols * TILE), view.cssH / (rows * TILE));
}

export function scaleFor(cam: Camera, view: Viewport, cols: number, rows: number): number {
  const fit = fitScale(view, cols, rows);
  switch (cam.zoom % ZOOM_STEPS) {
    case 1:
      return Math.max(fit, 1);
    case 2:
      return Math.max(fit, 2);
    default:
      return fit;
  }
}

export function isZoomed(cam: Camera): boolean {
  return cam.zoom % ZOOM_STEPS !== 0;
}

export function cycleZoom(cam: Camera): void {
  cam.zoom = (cam.zoom + 1) % ZOOM_STEPS;
  cam.vx = 0;
  cam.vy = 0;
}

export function zoomLabel(cam: Camera): string {
  return ZOOM_LABEL[cam.zoom % ZOOM_STEPS];
}

/** 指で動かしたら追従はやめる。猫より自分の見たい場所が優先 */
export function stopFollowing(cam: Camera): void {
  cam.follow = null;
}

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

function clampCamera(cam: Camera, view: Viewport, cols: number, rows: number): void {
  const world = worldSize(cols, rows);
  const visW = view.cssW / cam.scale;
  const visH = view.cssH / cam.scale;
  if (visW >= world.w) cam.x = (world.w - visW) / 2;
  else cam.x = clamp(cam.x, 0, world.w - visW);
  if (visH >= world.h) cam.y = (world.h - visH) / 2;
  else cam.y = clamp(cam.y, 0, world.h - visH);
}

/** 見えている範囲の中心を論理座標で */
export function centerOf(cam: Camera, view: Viewport): { x: number; y: number } {
  return { x: cam.x + view.cssW / cam.scale / 2, y: cam.y + view.cssH / cam.scale / 2 };
}

/** 中心を指定して動かす */
export function centerOn(cam: Camera, view: Viewport, x: number, y: number): void {
  cam.x = x - view.cssW / cam.scale / 2;
  cam.y = y - view.cssH / cam.scale / 2;
}

export interface FollowTarget {
  x: number;
  y: number;
}

/**
 * 毎フレーム呼ぶ。倍率の確定、追従、慣性、はみ出しの補正をまとめて行う。
 * dt は実時間の秒（シムの DT とは別物。カメラは演出なので可変で構わない）。
 */
export function updateCamera(
  cam: Camera,
  view: Viewport,
  cols: number,
  rows: number,
  dt: number,
  target: FollowTarget | null,
  dragging: boolean,
): void {
  const prevScale = cam.scale;
  cam.scale = scaleFor(cam, view, cols, rows);
  if (prevScale !== cam.scale) {
    // 倍率が変わっても、見ていた中心はそのまま保つ
    const c = { x: cam.x + view.cssW / prevScale / 2, y: cam.y + view.cssH / prevScale / 2 };
    centerOn(cam, view, c.x, c.y);
  }

  if (target && cam.follow !== null) {
    // 猫を画面の中心へ、ゆっくり追いつく
    const c = centerOf(cam, view);
    const k = Math.min(1, FOLLOW_LERP * dt);
    centerOn(cam, view, c.x + (target.x - c.x) * k, c.y + (target.y - c.y) * k);
    cam.vx = 0;
    cam.vy = 0;
  } else if (!dragging && (Math.abs(cam.vx) > MIN_SPEED || Math.abs(cam.vy) > MIN_SPEED)) {
    // 指を離したあとの滑り
    cam.x -= cam.vx * dt;
    cam.y -= cam.vy * dt;
    const decay = Math.pow(FRICTION, dt);
    cam.vx *= decay;
    cam.vy *= decay;
  } else if (!dragging) {
    cam.vx = 0;
    cam.vy = 0;
  }

  clampCamera(cam, view, cols, rows);
}

/** 指の動きぶんカメラを動かす（画面px → 論理px）。指に貼り付く1:1 */
export function panBy(cam: Camera, dxCss: number, dyCss: number): void {
  cam.x -= dxCss / cam.scale;
  cam.y -= dyCss / cam.scale;
}

/**
 * 指を離した時の勢いで滑らせる。
 * 速度は画面pxで受け取り、論理pxに直してから盛る。
 * こうしておくと、倍率を変えても「同じ勢いで振れば同じだけ画面が流れる」。
 */
export function flingBy(cam: Camera, vxCss: number, vyCss: number): void {
  const vx = (vxCss / cam.scale) * FLICK_BOOST;
  const vy = (vyCss / cam.scale) * FLICK_BOOST;
  cam.vx = clamp(vx, -MAX_FLING, MAX_FLING);
  cam.vy = clamp(vy, -MAX_FLING, MAX_FLING);
}

/** ポインタ座標（clientX/Y）を論理座標へ。実サイズ比を取る（§5.3） */
export function screenToWorld(
  canvas: HTMLCanvasElement,
  cam: Camera,
  clientX: number,
  clientY: number,
): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect();
  const cssX = ((clientX - rect.left) / rect.width) * canvas.clientWidth;
  const cssY = ((clientY - rect.top) / rect.height) * canvas.clientHeight;
  return { x: cssX / cam.scale + cam.x, y: cssY / cam.scale + cam.y };
}

export function worldToCell(x: number, y: number): { c: number; r: number } {
  return { c: Math.floor(x / TILE), r: Math.floor(y / TILE) };
}
